import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Autocomplete,
  Breadcrumbs,
  Link,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface ModulePermission {
  id: string;
  userId: string;
  moduleId: string;
  role: 'admin' | 'user';
  grantedAt: string;
  user: {
    id: string;
    displayName: string;
    keycloakUserId: string;
  };
}

interface Module {
  id: string;
  title: string;
  course: {
    title: string;
  };
}

interface User {
  id: string;
  displayName: string;
  keycloakUserId: string;
}

export default function AdminModulePermissionsPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<ModulePermission | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'user'>('user');
  const [userSearch, setUserSearch] = useState('');

  // Fetch module permissions
  const { data: permissionsData, isLoading } = useQuery({
    queryKey: ['module-permissions', moduleId],
    queryFn: async () => {
      const response = await api.get(`/admin/modules/${moduleId}/permissions`);
      return response.data as { module: Module; permissions: ModulePermission[] };
    },
    enabled: !!moduleId,
  });

  // Search users for autocomplete
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users-search', userSearch],
    queryFn: async () => {
      const response = await api.get('/admin/users', {
        params: { search: userSearch, limit: 10 },
      });
      return response.data.users as User[];
    },
    enabled: userSearch.length >= 2,
  });

  // Add permission mutation
  const addPermissionMutation = useMutation({
    mutationFn: async (data: { userId: string; role: string }) => {
      const response = await api.post(`/admin/modules/${moduleId}/permissions`, data);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung hinzugefuegt', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['module-permissions', moduleId] });
      handleCloseAddDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Hinzufuegen', { variant: 'error' });
    },
  });

  // Update permission mutation
  const updatePermissionMutation = useMutation({
    mutationFn: async (data: { userId: string; role: string }) => {
      const response = await api.patch(`/admin/modules/${moduleId}/permissions/${data.userId}`, { role: data.role });
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung aktualisiert', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['module-permissions', moduleId] });
      handleCloseEditDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Aktualisieren', { variant: 'error' });
    },
  });

  // Delete permission mutation
  const deletePermissionMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/admin/modules/${moduleId}/permissions/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung entfernt', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['module-permissions', moduleId] });
      handleCloseDeleteDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Entfernen', { variant: 'error' });
    },
  });

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedUser(null);
    setSelectedRole('user');
    setUserSearch('');
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedPermission(null);
    setSelectedRole('user');
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedPermission(null);
  };

  const handleEditPermission = (permission: ModulePermission) => {
    setSelectedPermission(permission);
    setSelectedRole(permission.role);
    setEditDialogOpen(true);
  };

  const handleDeletePermission = (permission: ModulePermission) => {
    setSelectedPermission(permission);
    setDeleteDialogOpen(true);
  };

  const handleAddPermission = () => {
    if (selectedUser) {
      addPermissionMutation.mutate({
        userId: selectedUser.id,
        role: selectedRole,
      });
    }
  };

  const handleUpdatePermission = () => {
    if (selectedPermission) {
      updatePermissionMutation.mutate({
        userId: selectedPermission.userId,
        role: selectedRole,
      });
    }
  };

  const handleConfirmDelete = () => {
    if (selectedPermission) {
      deletePermissionMutation.mutate(selectedPermission.userId);
    }
  };

  const module = permissionsData?.module;
  const permissions = permissionsData?.permissions || [];

  // Filter out users that already have permissions
  const existingUserIds = new Set(permissions.map(p => p.userId));
  const availableUsers = (usersData || []).filter(u => !existingUserIds.has(u.id));

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!module) {
    return (
      <Box>
        <Alert severity="error">Modul nicht gefunden</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin')} sx={{ mt: 2 }}>
          Zurueck
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/admin')}
        >
          Admin
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/admin/modules')}
        >
          Module
        </Link>
        <Typography color="text.primary">{module.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Berechtigungen</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {module.course.title} / {module.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
          >
            Zurueck
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Berechtigung hinzufuegen
          </Button>
        </Box>
      </Box>

      {/* Permissions Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Benutzer</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Vergeben am</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Keine Berechtigungen vergeben. Fuegen Sie Benutzer hinzu, um ihnen Zugang zu diesem Modul zu geben.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                permissions.map((permission) => (
                  <TableRow key={permission.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: permission.role === 'admin' ? 'error.main' : 'primary.main', width: 36, height: 36 }}>
                          {permission.user.displayName?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {permission.user.displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={permission.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                        label={permission.role === 'admin' ? 'Admin' : 'Benutzer'}
                        color={permission.role === 'admin' ? 'error' : 'primary'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(permission.grantedAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Rolle aendern">
                        <IconButton size="small" onClick={() => handleEditPermission(permission)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Entfernen">
                        <IconButton size="small" onClick={() => handleDeletePermission(permission)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Admin:</strong> Kann das Modul verwalten und Inhalte bearbeiten.<br />
          <strong>Benutzer:</strong> Kann das Modul nutzen (Lernen, Quiz, Fortschritt).
        </Typography>
      </Alert>

      {/* Add Permission Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Berechtigung hinzufuegen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) => option.displayName || ''}
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              onInputChange={(_, newValue) => setUserSearch(newValue)}
              loading={userSearch.length >= 2 && !usersData}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Benutzer suchen"
                  placeholder="Name eingeben..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearch.length >= 2 && !usersData ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {option.displayName?.charAt(0) || 'U'}
                    </Avatar>
                    <Typography>{option.displayName}</Typography>
                  </Box>
                </li>
              )}
              noOptionsText={userSearch.length < 2 ? 'Mindestens 2 Zeichen eingeben' : 'Keine Benutzer gefunden'}
            />

            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={selectedRole}
                label="Rolle"
                onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'user')}
              >
                <MenuItem value="user">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Benutzer
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon fontSize="small" />
                    Admin
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddPermission}
            disabled={!selectedUser || addPermissionMutation.isPending}
          >
            {addPermissionMutation.isPending ? <CircularProgress size={20} /> : 'Hinzufuegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permission Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Rolle aendern</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Berechtigung fuer: <strong>{selectedPermission?.user.displayName}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={selectedRole}
                label="Rolle"
                onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'user')}
              >
                <MenuItem value="user">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Benutzer
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon fontSize="small" />
                    Admin
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpdatePermission}
            disabled={updatePermissionMutation.isPending}
          >
            {updatePermissionMutation.isPending ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Berechtigung entfernen</DialogTitle>
        <DialogContent>
          <Typography>
            Moechten Sie die Berechtigung fuer <strong>{selectedPermission?.user.displayName}</strong> wirklich entfernen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Der Benutzer verliert den Zugang zu diesem Modul.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deletePermissionMutation.isPending}
          >
            {deletePermissionMutation.isPending ? <CircularProgress size={20} /> : 'Entfernen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
